const MAX_PHOTOS = 3;
const MAX_PHOTO_BYTES = 15 * 1024 * 1024;
const CHUNK_BYTES = 5 * 1024 * 1024;
const allowedExtensions = new Set(['jpg', 'jpeg', 'png', 'heic', 'heif']);
const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/heic', 'image/heif']);

export function renderPhotoSection() {
  return `
    <div class="field photo-field">
      <div id="photo-list" class="photo-list" aria-live="polite"></div>
      <input id="photo-input" type="file" accept="image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif" multiple hidden>
      <button id="photo-add-button" class="photo-add-button" type="button" aria-describedby="photo-error">Add Photo<span class="photo-button-open-paren">(</span>s)</button>
      <div id="photo-error" class="field-error photo-error" role="alert"></div>
    </div>
  `;
}

export function createPhotoAttachmentController(root, options = {}) {
  const input = root.querySelector('#photo-input');
  const addButton = root.querySelector('#photo-add-button');
  const list = root.querySelector('#photo-list');
  const error = root.querySelector('#photo-error');
  const getAccessToken = typeof options.getAccessToken === 'function'
    ? options.getAccessToken
    : async () => '';
  const items = [];
  let draftToken = '';
  let locked = false;
  let activeUploadPromise = null;

  addButton.addEventListener('click', () => !locked && !activeUploadPromise && input.click());
  input.addEventListener('change', () => {
    addFiles([...input.files]);
    input.value = '';
  });
  list.addEventListener('click', (event) => {
    const removeButton = event.target.closest('[data-remove-photo]');
    const retryButton = event.target.closest('[data-retry-photo]');
    if (removeButton && !locked && !activeUploadPromise) {
      removeItem(Number(removeButton.dataset.removePhoto));
    }
    if (retryButton && !locked && !activeUploadPromise) {
      const item = items[Number(retryButton.dataset.retryPhoto)];
      if (item) {
        error.textContent = '';
        retryUploads(item);
      }
    }
  });

  function addFiles(files) {
    error.textContent = '';
    if (!files.length) return;
    if (items.length + files.length > MAX_PHOTOS) {
      error.textContent = 'You can choose up to 3 photos.';
      return;
    }
    const checked = files.map(validateFile);
    const invalid = checked.find((result) => !result.valid);
    if (invalid) {
      error.textContent = invalid.message;
      return;
    }
    resetDraft();
    for (const file of files) {
      items.push({
        file,
        previewUrl: URL.createObjectURL(file),
        status: 'waiting',
        progress: 0,
        nextOffset: 0,
        uploadUrl: '',
        itemId: '',
        error: ''
      });
    }
    render();
    startBackgroundUpload();
  }

  function removeItem(index) {
    const [removed] = items.splice(index, 1);
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    resetDraft();
    error.textContent = '';
    render();
    if (items.length) startBackgroundUpload();
  }

  function resetDraft() {
    draftToken = '';
    for (const item of items) {
      item.status = 'waiting';
      item.progress = 0;
      item.nextOffset = 0;
      item.uploadUrl = '';
      item.itemId = '';
      item.error = '';
    }
  }

  function clear() {
    for (const item of items) URL.revokeObjectURL(item.previewUrl);
    items.splice(0);
    resetDraft();
    error.textContent = '';
    render();
  }

  async function ensureUploaded(accessToken, onStatus = () => {}) {
    if (!items.length) return null;
    if (activeUploadPromise) {
      onStatus('Finishing photo uploads...');
      await activeUploadPromise;
    }
    const failedItem = items.find((item) => item.status === 'failed');
    if (failedItem) {
      throw new Error(failedItem.error || 'A photo could not be uploaded. Use Retry and try again.');
    }
    if (!draftToken || items.some((item) => !item.itemId)) {
      onStatus('Uploading photos before submission...');
      await startBackgroundUpload(accessToken);
    }
    onStatus('Submitting form...');
    return {
      draftToken,
      attachments: items.map((item) => ({ itemId: item.itemId }))
    };
  }

  function setLocked(value) {
    locked = Boolean(value);
    render();
  }

  function restartUploads() {
    resetDraft();
    render();
    if (items.length) startBackgroundUpload();
  }

  function startBackgroundUpload(accessToken = '') {
    if (!items.length) return Promise.resolve(null);
    if (activeUploadPromise) return activeUploadPromise;
    return trackUpload(runUploadBatch(accessToken));
  }

  function retryUploads(item) {
    item.status = 'retrying';
    item.error = '';
    render();
    let retry;
    if (item.uploadUrl) {
      retry = runUploadBatch('', item);
    } else {
      resetDraft();
      retry = runUploadBatch('');
    }
    trackUpload(retry);
  }

  function trackUpload(uploadPromise) {
    let trackedPromise;
    trackedPromise = Promise.resolve(uploadPromise)
      .catch((uploadError) => {
        if (!items.some((item) => item.status === 'failed')) {
          for (const item of items) {
            if (!item.itemId) {
              item.status = 'failed';
              item.error = uploadError.message || 'Upload failed.';
            }
          }
        }
        error.textContent = uploadError.message || 'A photo could not be uploaded.';
        throw uploadError;
      })
      .finally(() => {
        if (activeUploadPromise === trackedPromise) activeUploadPromise = null;
        render();
      });
    activeUploadPromise = trackedPromise;
    render();
    trackedPromise.catch(() => {});
    return trackedPromise;
  }

  async function runUploadBatch(accessToken = '', retryItem = null) {
    const token = accessToken || await getAccessToken();
    if (!token) {
      const authenticationError = new Error('Please sign in again before uploading photos.');
      authenticationError.status = 401;
      throw authenticationError;
    }
    error.textContent = '';
    if (!draftToken) await createSessions(token);
    if (retryItem?.uploadUrl && !retryItem.itemId) {
      await refreshUploadOffset(retryItem);
    }
    for (const item of items) {
      if (!item.itemId) await uploadItem(item);
    }
    return {
      draftToken,
      attachments: items.map((item) => ({ itemId: item.itemId }))
    };
  }

  async function createSessions(accessToken) {
    for (const item of items) {
      if (!item.itemId) item.status = 'preparing';
    }
    render();
    const response = await fetch('/api/attachment-sessions', {
      method: 'POST',
      headers: {
        'X-WB-Parts-Access-Token': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        companySlug: root.dataset.companySlug,
        files: items.map(({ file }) => ({ name: file.name, size: file.size, type: file.type }))
      })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.ok || !Array.isArray(result.uploads)) {
      const sessionError = new Error(result.message || 'Secure photo uploads could not be prepared.');
      sessionError.status = response.status;
      throw sessionError;
    }
    if (result.uploads.length !== items.length) {
      throw new Error('The photo upload service returned an invalid response.');
    }
    draftToken = result.draftToken;
    result.uploads.forEach((upload, index) => {
      items[index].uploadUrl = upload.uploadUrl;
      items[index].status = 'waiting';
    });
    render();
  }

  async function uploadItem(item) {
    if (!item.uploadUrl) {
      throw new Error('Submit the form again to restart the secure photo upload.');
    }
    item.error = '';
    let offset = Math.min(item.nextOffset || 0, item.file.size);
    try {
      while (offset < item.file.size) {
        item.status = 'uploading';
        render();
        const endExclusive = Math.min(offset + CHUNK_BYTES, item.file.size);
        const chunk = item.file.slice(offset, endExclusive);
        const result = await sendChunk(item, chunk, offset, endExclusive);
        if (result.status === 200 || result.status === 201) {
          item.itemId = result.body.id || '';
          if (!item.itemId) throw new Error('SharePoint did not return the uploaded photo identifier.');
          item.progress = 100;
          item.status = 'uploaded';
          render();
          return;
        }
        if (result.status !== 202) {
          if (result.status === 404 || result.status === 410) {
            resetDraft();
            throw new Error('The photo upload session expired. Submit the form again to restart it.');
          }
          throw new Error('This photo could not be uploaded. Use Retry to try this file again.');
        }
        const nextOffset = parseNextOffset(result.body);
        if (nextOffset <= offset || nextOffset >= item.file.size) {
          throw new Error('SharePoint returned an invalid upload position. Use Retry to resynchronize this photo.');
        }
        offset = nextOffset;
        item.nextOffset = offset;
        item.progress = Math.round((offset / item.file.size) * 100);
      }
    } catch (uploadError) {
      item.status = 'failed';
      item.error = uploadError.message || 'Upload failed.';
      render();
      throw uploadError;
    }
  }

  async function refreshUploadOffset(item) {
    if (!item.uploadUrl) {
      throw new Error('Submit the form again to restart the secure photo upload.');
    }
    const result = await getUploadStatus(item.uploadUrl);
    if (result.status === 404 || result.status === 410) {
      resetDraft();
      throw new Error('The photo upload session expired. Submit the form again to restart it.');
    }
    if (result.status !== 200) {
      throw new Error('SharePoint could not resume this photo upload. Try again.');
    }
    const nextOffset = parseNextOffset(result.body);
    if (nextOffset < 0 || nextOffset >= item.file.size) {
      throw new Error('SharePoint returned an invalid upload position. Submit the form again to restart it.');
    }
    item.nextOffset = nextOffset;
    item.progress = Math.round((nextOffset / item.file.size) * 100);
  }

  function getUploadStatus(uploadUrl) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('GET', uploadUrl);
      request.addEventListener('load', () => {
        let body = {};
        try { body = JSON.parse(request.responseText || '{}'); } catch { body = {}; }
        resolve({ status: request.status, body });
      });
      request.addEventListener('error', () => reject(new Error('The network interrupted the photo retry. Try again.')));
      request.addEventListener('abort', () => reject(new Error('The photo retry was canceled.')));
      request.send();
    });
  }

  function sendChunk(item, chunk, start, endExclusive) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('PUT', item.uploadUrl);
      request.setRequestHeader('Content-Range', `bytes ${start}-${endExclusive - 1}/${item.file.size}`);
      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          item.progress = Math.min(99, Math.round(((start + event.loaded) / item.file.size) * 100));
          updateItemProgress(item);
        }
      });
      request.addEventListener('load', () => {
        let body = {};
        try { body = JSON.parse(request.responseText || '{}'); } catch { body = {}; }
        resolve({ status: request.status, body });
      });
      request.addEventListener('error', () => reject(new Error('The network interrupted this photo upload. Use Retry to continue.')));
      request.addEventListener('abort', () => reject(new Error('The photo upload was canceled.')));
      request.send(chunk);
    });
  }

  function updateItemProgress(item) {
    const index = items.indexOf(item);
    const progress = list.querySelector(`[data-photo-progress="${index}"]`);
    const status = list.querySelector(`[data-photo-status="${index}"]`);
    if (progress) progress.value = item.progress;
    if (status) status.textContent = statusLabel(item);
  }

  function render() {
    const clearButton = root.querySelector('#clear-button');
    const submitButton = root.querySelector('#submit-button');
    const uploading = Boolean(activeUploadPromise);
    if (clearButton) clearButton.disabled = locked || uploading;
    if (submitButton) submitButton.disabled = locked;
    const atPhotoLimit = items.length >= MAX_PHOTOS;
    input.disabled = locked || uploading || atPhotoLimit;
    addButton.disabled = input.disabled;
    addButton.hidden = atPhotoLimit;
    list.innerHTML = items.map((item, index) => `
      <article class="photo-item${item.status === 'uploaded' ? ' photo-item-uploaded' : ''}">
        <div class="photo-preview-wrap">
          <img class="photo-preview" src="${escapeAttribute(item.previewUrl)}" alt="Preview of ${escapeHtml(item.file.name)}">
          <span class="photo-preview-fallback" hidden aria-hidden="true">IMG</span>
        </div>
        <div class="photo-details">
          <div class="photo-heading">
            <strong title="${escapeAttribute(item.file.name)}">${escapeHtml(item.file.name)}</strong>
            <span class="photo-status" data-photo-status="${index}">${item.status === 'uploaded' ? '<span class="photo-status-check" aria-hidden="true">&#10003;</span>' : ''}${statusLabel(item)}</span>
          </div>
          <progress data-photo-progress="${index}" max="100" value="${item.progress}" aria-label="Upload progress for ${escapeAttribute(item.file.name)}"></progress>
          ${item.error ? `<small class="field-error">${escapeHtml(item.error)}</small>` : ''}
        </div>
        <div class="photo-actions">
          ${item.status === 'failed' ? `<button class="photo-action" data-retry-photo="${index}" type="button" ${locked || uploading ? 'disabled' : ''}>Retry</button>` : ''}
          <button class="photo-action" data-remove-photo="${index}" type="button" ${locked || uploading ? 'disabled' : ''}>Remove</button>
        </div>
      </article>
    `).join('');
    for (const preview of list.querySelectorAll('.photo-preview')) {
      preview.addEventListener('error', () => {
        preview.hidden = true;
        preview.nextElementSibling.hidden = false;
      }, { once: true });
    }
  }

  render();
  return {
    clear,
    ensureUploaded,
    restartUploads,
    setLocked,
    get busy() { return Boolean(activeUploadPromise); },
    get count() { return items.length; }
  };
}

function validateFile(file) {
  const extension = String(file.name || '').toLowerCase().split('.').pop();
  if (!allowedExtensions.has(extension) || (file.type && !allowedTypes.has(file.type.toLowerCase()))) {
    return { valid: false, message: `${file.name || 'This file'} must be a JPEG, PNG, HEIC, or HEIF photo.` };
  }
  if (!Number.isSafeInteger(file.size) || file.size < 1 || file.size > MAX_PHOTO_BYTES) {
    return { valid: false, message: `${file.name || 'This photo'} is too large. Choose a photo that is 15 MB or smaller.` };
  }
  return { valid: true };
}

function parseNextOffset(body) {
  const range = Array.isArray(body.nextExpectedRanges) ? body.nextExpectedRanges[0] : '';
  const start = String(range || '').split('-')[0];
  if (!/^\d+$/.test(start)) return -1;
  const value = Number(start);
  return Number.isSafeInteger(value) && value >= 0 ? value : -1;
}

function statusLabel(item) {
  return {
    preparing: 'Uploading',
    waiting: 'Uploading',
    uploading: 'Uploading',
    retrying: 'Uploading',
    uploaded: 'Uploaded',
    failed: 'Failed'
  }[item.status] || 'Uploading';
}

function escapeHtml(value) {
  return String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[character]);
}

function escapeAttribute(value) {
  return escapeHtml(value);
}
